<script setup>
//vue imports
import { ref, watch } from "vue";
import { useForm } from "@inertiajs/vue3";
import { Head } from "@inertiajs/vue3";
import Button from "./Components/Button.vue";
import { router } from "@inertiajs/vue3";
import Faq from './Components/Faq.vue';
import Subfooter from './Includes/Subfooter.vue';
import Select from './Components/Form/Select.vue';
import SaveButton from "./Components/Form/SaveButton.vue";
import CustomLink from "./Components/CustomLink.vue";
import Vehicle from "./Components/Vehicle.vue";

const eurFormatter = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
});

const props = defineProps({
    vehicles: Array,
    latestVehicles: Array
});

const minYear = Math.min(...props.vehicles.map(vehicle => vehicle.year));
const maxYear = Math.max(...props.vehicles.map(vehicle => vehicle.year));
const constructionYears = ref(
    Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(year => ({ id: year, label: year }))
);

const mileages = ref(
    Array.from({ length: 400000 / 10000 }, (_, i) => i * 10000).map(mileage => ({ id: mileage, label: mileage.toLocaleString() + ' km' }))
);

const prices = ref(
    Array.from({ length: 150000 / 10000 }, (_, i) => i * 10000).map(price => ({ id: price, label: eurFormatter.format(price) }))
);

const searchForm = useForm({
    brand: null,
    model: null,
    type: null,
    price: [null, null],
    construction_year: [null, null],
    mileage: [null, null],
    fuel: null,
    transmission: null,
    body: null
})

//watch all form values
const brandOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.brand))].map(brand => ({ id: brand, label: brand })));

const modelOptions = ref([]);
watch(
    () => searchForm.brand,
    (newBrand) => {
        if (newBrand) {
            modelOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === newBrand)
                    .map(vehicle => vehicle.model)
            )].map(model => ({ id: model, label: model }));
        } else {
            modelOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when brand has changed, model should be reset
        searchForm.model = null;
        searchForm.type = null;
    }
);

const typeOptions = ref([]);
watch(
    () => searchForm.model,
    (newModel) => {
        if (newModel) {
            typeOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === searchForm.brand)
                    .filter(vehicle => vehicle.model === newModel)
                    .map(vehicle => vehicle.type)
            )].map(type => ({ id: type, label: type }));
        } else {
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when model has changed, type should be reset
        searchForm.type = null;
    }
);


const bodyTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.body))].map(body => ({ id: body, label: body })));
const fuelTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.fuel))].map(fuel => ({ id: fuel, label: fuel })));
const transmissionTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.transmission))].map(transmission => ({ id: transmission, label: transmission })));

function submit() {
    const filterParams = {
        brand: searchForm.brand,
        model: searchForm.model,
        type: searchForm.type,
        body: searchForm.body,
        fuel: searchForm.fuel,
        transmission: searchForm.transmission,
        price: searchForm.price,
        construction_year: searchForm.construction_year,
        mileage: searchForm.mileage
    };

    localStorage.setItem('filterParams', JSON.stringify(filterParams));

    router.visit('/aanbod');
}
</script>

<template>
    <Head>
        <title>Zwagers Automotive - Uw Partner voor Gebruikte Auto's van Kwaliteit</title>
        <meta name="description" content="Zwagers Automotive: specialist in betrouwbare occasions, uitstekende service en persoonlijk advies. Ontdek ons ruime aanbod gebruikte auto's, transparante werkwijze en klantgerichte aanpak. Jouw partner voor kwaliteit en vertrouwen in mobiliteit." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Zwagers Automotive - Waar jouw rijwens werkelijkheid wordt. Vertrouwen, kwaliteit en een breed aanbod occasions voor elk budget." />
        <meta property="og:description" content="Zwagers Automotive: waar jouw rijwens werkelijkheid wordt. Ontdek een breed aanbod betrouwbare occasions, uitstekende service en persoonlijk advies. Vind de perfecte auto die past bij jouw wensen en budget." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/" />
        <meta property="og:image" content="/img/showroom/zwagers_showroom_35.jpg" />
        <!-- Pas de URL aan naar een aantrekkelijke afbeelding, zoals een auto of showroom -->
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Zwagers Automotive - Waar jouw rijwens werkelijkheid wordt. Vertrouwen, kwaliteit en een breed aanbod occasions voor elk budget." />
        <meta name="twitter:description" content="Zwagers Automotive: waar jouw rijwens werkelijkheid wordt. Ontdek een breed aanbod betrouwbare occasions, uitstekende service en persoonlijk advies. Vind de perfecte auto die past bij jouw wensen en budget." />
        <meta name="twitter:image" content="/img/showroom/zwagers_showroom_35.jpg" />
        <!-- Pas de URL aan naar een aantrekkelijke afbeelding -->
    </Head>

    <section class="flex flex-col bg-black lg:-space-y-56 w-full">
        <!--hero-->
        <div id="hero" class="h-full text-white">
            <div class="relative lg:h-screen lg:min-h-[768px] max-h-[1200px] flex flex-col">
                <video :src="'/videos/zwagers_automotive_hero.webm'" autoplay loop muted playsinline class="opacity-60 absolute w-full h-full aspect-video object-cover" />
                <!-- <img src="/public/img/showroom/zwagers_showroom_35.jpg" alt="Zwagers Automotive" class="opacity-60 absolute w-full h-full aspect-video object-cover" /> -->
                <div class="z-20 flex justify-center h-full">
                    <div class="w-11/12 px:0 md:px-6 xl:px-0 lg:h-5/6 max-w-6xl lg:min-h-[672px] py-24 lg:py-0 lg:pt-24">
                        <div class="flex flex-col gap-10 h-full justify-end pt-20">
                            <h1 class="text-shadow w-full md:w-9/12 xl:w-8/12">Vertrouwen, <br>kwaliteit & zekerheid</h1>
                            <p class="text-shadow w-full md:w-9/12 xl:w-8/12">
                                Of je nu droomt van een ruime gezinsauto, 
                                een sportieve rijervaring of je allereerste eigen wagen, wij bieden een zorgvuldig samengesteld aanbod aan occasions. 
                                Ons assortiment is breed en divers, zodat er altijd een auto is die perfect aansluit bij jouw wensen én budget.
                            </p>
                            <div class="flex sm:flex-row flex-col gap-6">
                                <Button href="/aanbod" type="href" class="primary-button">Bekijk ons aanbod</Button>
                                <Button href="/zoeken" type="href" class="secondary-white-button">Zoeken</Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <main class="flex flex-col py-10 md:py-20 lg:py-28 gap-28">
        <section id="filters">
            <div class="flex justify-center">
                <div class="grid grid-cols-1 justify-start w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl space-y-2 lg:space-y-10">
                    <h1>Vind jouw auto</h1>
                    <form @submit.prevent="submit">
                        <div class="mt-4 grid sm:grid-cols-12 text-sm font-normal text-gray-900 gap-3 md:gap-5">
                            <div class="col-span-full sm:col-span-4"> 
                                <Select v-model="searchForm.brand" :options="brandOptions" label="Merk" valueName="label" clear valueId="id" placeholder="Selecteer een merk..." />
                            </div>
                            <div class="col-span-full sm:col-span-4">
                                <Select v-model="searchForm.model" :options="modelOptions" label="Model" valueName="label" clear valueId="id" placeholder="Selecteer een model..." />
                            </div>
                            <div class="col-span-full sm:col-span-4">
                                <Select v-model="searchForm.type" :options="typeOptions" label="Variant" valueName="label" clear valueId="id" placeholder="Selecteer een type..." />
                            </div>
                            <div class="col-span-full sm:col-span-4">
                                <Select v-model="searchForm.body" :options="bodyTypes" label="Carrosserie" valueName="label" clear valueId="id" placeholder="Selecteer een carrosserie..." />
                            </div>
                            <div class="col-span-full sm:col-span-4">
                                <Select v-model="searchForm.fuel" :options="fuelTypes" label="Brandstof" valueName="label" clear valueId="id" placeholder="Selecteer een brandstof..." />
                            </div>
                            <div class="col-span-full sm:col-span-4">
                                <Select v-model="searchForm.transmission" :options="transmissionTypes" label="Transmissie" valueName="label" clear valueId="id" placeholder="Selecteer een transmissie..." />
                            </div>
                            <div class="col-span-full sm:col-span-4 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-0 lg:gap-4">
                                <div>
                                    <Select :options="prices" valueName="label" valueId="id" label="Prijs" v-model="searchForm.price[0]" placeholder="Van" />
                                </div>
                                <div class="content-end">
                                    <Select :options="prices" valueName="label" valueId="id" label=" " v-model="searchForm.price[1]" placeholder="Tot" />
                                </div>
                            </div>
                            <div class="col-span-full sm:col-span-4 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-0 lg:gap-4">
                                <div>
                                    <Select v-model="searchForm.construction_year[0]" :options="constructionYears" label="Bouwjaar" valueName="label" valueId="id" placeholder="Van" />
                                </div>
                                <div class="content-end">
                                    <Select v-model="searchForm.construction_year[1]" :options="constructionYears" label="" valueName="label" valueId="id" placeholder="Tot" />
                                </div>
                            </div>
                            <div class="col-span-full sm:col-span-4 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-0 lg:gap-4">
                                <div>
                                    <Select v-model="searchForm.mileage[0]" :options="mileages" label="Kilometerstand" valueName="label" valueId="id" placeholder="Van" />
                                </div>
                                <div class="content-end">
                                    <Select v-model="searchForm.mileage[1]" :options="mileages" label="" valueName="label" valueId="id" placeholder="Tot" />
                                </div>
                            </div>

                            <div class="w-full flex justify-end col-span-full">
                                <SaveButton class="primary-button" text="Zoeken" />
                            </div>
                        </div>
                    </form>  
                </div>
            </div>
        </section>
        <div class="flex place-content-center">
            <div class="flex flex-wrap w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl mx-auto gap-9">
                <h1 class="font-bold">Nieuw binnengekomen</h1>
                <!--stuur data voor homepage naar model.vue-->
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    <div v-for="vehicle in latestVehicles" :key="vehicle.id">
                        <Vehicle :vehicle="vehicle" />
                    </div>
                </div>
                <CustomLink href="/aanbod" class="primary-button ml-auto mt-auto">Bekijk alles</CustomLink>
            </div>
        </div>
    </main>
    <Faq />
    <Subfooter />
</template>
